import { Box, Link, SvgIcon, Typography } from "@mui/material";
import { OtherFaceDetectionEvent, TargetFaceDetectionEvent } from "@verity/api-client/types";
import { useResizeListener } from "@verity/components";
import {
	CoreChip,
	CoreChipProps,
	CoreFilterButton,
	CoreFilterSectionOption,
	CoreIconButton,
	CoreModal,
	CoreSkeletonWrapper,
} from "@verity/components/core";
import {
	FaceDetectionEventSchema,
	ObjectDetectionEventSchema,
	RestrictedDeviceLoggedEvent,
	ZodToReadable,
} from "@yujacorp/verity-api-contract";
import { Match } from "effect";
import { Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Trans } from "react-i18next";
import CompositeTable, {
	CompositeTableBodyRow,
	CompositeTableCell,
	CompositeTableContainer,
	CompositeTableHeaderColumn,
	CompositeTableHeaderRow,
	CompositeTableNoResults,
	CompositeTableSkeleton,
} from "../../../../../components/composite-table";
import apiClient from "../../../../../helpers/api-client";
import { getDurationAriaLabelFromMs } from "../../../../../helpers/time";
import i18n from "../../../../../i18n";
import Pencil from "../../../../../images/pencil.svg?react";
import SignatureSymbol from "../../../../../images/reports/signature.svg?react";
import EnableGeofenceSymbol from "../../../../../images/settings/enable-geofence.svg?react";
import AccessCodeSymbol from "../../../../images/accessCode.svg?react";
import CaretDown from "../../../../images/caretDown.svg?react";
import CaretRight from "../../../../images/caretRight.svg?react";
import Comment from "../../../../images/comment.svg?react";
import ConnectionCheckSymbol from "../../../../images/connectionCheckSymbol.svg?react";
import ConnectionStatusConnectedSymbol from "../../../../images/connectionStatusConnected.svg?react";
import ConnectionStatusDisconnectedSymbol from "../../../../images/connectionStatusDisconnected.svg?react";
import CopySymbol from "../../../../images/copySymbol.svg?react";
import CreateNoteSymbol from "../../../../images/create-note-symbol.svg?react";
import EndRecordingSymbol from "../../../../images/endRecordingSymbol.svg?react";
import EnterSymbol from "../../../../images/enter.svg?react";
import FaceDetectionSymbol from "../../../../images/face-detection.svg?react";
import HumanFlagSymbol from "../../../../images/humanFlag.svg?react";
import IncognitoSymbol from "../../../../images/incognitoSymbol.svg?react";
import InterruptResumeSymbol from "../../../../images/interrupt-resume.svg?react";
import InterruptTriggerSymbol from "../../../../images/interrupt-trigger.svg?react";
import JoinedWithoutProctorSymbol from "../../../../images/joined-without-proctor.svg?react";
import KeystrokeSymbol from "../../../../images/keystroke.svg?react";
import LeaveSymbol from "../../../../images/leave.svg?react";
import MicrophoneSymbol from "../../../../images/microphoneSymbol.svg?react";
import MonitorSymbol from "../../../../images/monitorSymbol.svg?react";
import NewTabSymbol from "../../../../images/newTabSymbol.svg?react";
import NoSubmissionSymbol from "../../../../images/no-submission.svg?react";
import ObjectDetectionSymbol from "../../../../images/object-detection.svg?react";
import PlayButton from "../../../../images/playButton.svg?react";
import ScreenshotSymbol from "../../../../images/screenshotSymbol.svg?react";
import StartQuizSymbol from "../../../../images/startQuizSymbol.svg?react";
import EndQuizSymbol from "../../../../images/stopQuizSymbol.svg?react";
import SystemResourcesSymbol from "../../../../images/system-resources.svg?react";
import WebcamEndSymbol from "../../../../images/webcamEndSymbol.svg?react";
import WebcamSymbol from "../../../../images/webcamSymbol.svg?react";
import WebNavigationSymbol from "../../../../images/webNavigationSymbol.svg?react";
import { binSearch } from "../../../../utilities/timestampBinSearch";
import { TimestampsArr, VideoEvent } from "../../../../utilities/useTimestamps";
import { formatEventTimestamp } from "../../../ProctoringResultsPage/ProctoringEventsTable";
import ResolveModal from "../../../ResolveModal";
import { MajorFlagLevel, MinorFlagLevel, NoneFlagLevel, SevereFlagLevel } from "../../SelectionMenu";
import FlagFilterLayout, { type FlagFilterLevel } from "../components/FlagFilterLayout.js";
import RestrictDevicesSymbol from "../images/restrict-devices.svg?react";
import { VideoPlaybackAction } from "../ProctoringResultView";
import { useCollaborationRowAction } from "./CollaborationDetection/CollaborationDetectedEventRow";
import {
	CollaborationParticipant,
	CollaborationPartnerSummary,
	groupCollaborationEventsByPartner,
} from "./CollaborationDetection/collaborationEvents";
import { EditCustomEventModal } from "./CustomEventModal";
import { InstalledExtensionsModal } from "./InstalledExtensionsModal";
import {
	RESOLVE_REVEAL_CLASS,
	RESOLVE_REVEAL_HIDE_CLASS,
	RESOLVE_REVEAL_TRANSITION_MS,
	resolveRevealBaseSx,
	resolveRevealHoverSx,
} from "./resolveReveal";
import { RunningApplicationsModal } from "./RunningApplicationsModal";
import { FILTER_EVENTS, getEventFilterCheckboxes } from "./Selector/EventTypeSelector";
import { processKeypressData } from "./timestamp-download-utils";
import "./TimestampLinks.css";

type TableSortBy = "timestamp" | "description" | "level";

export const validEvents = {
	system: {
		"connection status": true,
		"block applications": true,
		"applications activity": false,
		"system resources": true,
		"connection check": true,
		"not in geofence": true,
		"location unknown": true,
		"not in ip address range": true,
		"ip address unknown": true,
		"installed extensions snapshot": true,
		"running applications snapshot": true,
	},
	tab: {
		disable: false,
		"close all": false,
		"close one": false,
		create: false,
		focus: true,
		whitelist: false,
	},
	"user action": {
		"side panel detected": true,
		"right click blocked": true,
		"printing blocked": true,
		"toggled block mobile apps": true,
		"decoy site viewed": true,
		"typed signature": true,
	},
	screenshot: {
		disable: false,
		keypress: true,
	},
	session: {
		"start quiz": true,
		"end quiz": true,
		"start proctoring": true,
		"end proctoring": true,
		"start recording": true,
		"upload incomplete": true,
		"end recording": true,
		"force stop proctoring": true,
		"no submission": true,
	},
	ephemeral: {
		"missing end quiz verification": true,
	},
	monitor: {
		"enable detection": true,
		check: true,
		"detect change": true,
	},
	clipboard: {
		disable: false,
		clear: true,
		keypress: true,
	},
	incognito: {
		"check permission": true,
	},
	"web navigation": {
		webNavigation: true,
		webRequest: false,
		"blocked navigation": true,
		"connection check": true,
	},
	web_navigation: {
		web_navigation: true,
		webRequest: false,
		"connection check": true,
		"blocked navigation": true,
	},
	"access code": {
		autofill: true,
		"save password disabled": false,
		"save password reset": false,
	},
	// anomalies:
	video_error: {
		video_error: true,
	},
	head_movements: {
		head_movements: true,
	},
	"face detection": {
		other: true,
		target: true,
	} satisfies Record<OtherFaceDetectionEvent["name"] | TargetFaceDetectionEvent["name"], boolean>,
	"object detection": {
		computer: true,
		phone: true,
		book: true,
	},
	interrupt: {
		resume: true,
		trigger: true,
	},
	live_session_note: {
		create: true,
	},
	keyboard: {
		keypress: false,
	},
	live: {
		"audio bridge": true,
		"proctor joined": true,
		"proctor left": true,
		"join session": true,
		"no proctors": true,
	},
	worker: {
		face: true,
		object: true,
	},
	"machine-learning": {
		"face-detection": true,
		"object-detection": true,
		"flagged-keyword": true,
	},
	id: {
		"face id mismatch": true,
		"bypassed ai detection": true,
	},
	assessment: {
		"collaboration detected": true,
		"essay collaboration detected": true,
	},
	custom: {
		live: true,
		review: true,
	},
	"restricted-device": {
		state: true,
		connect: true,
		disconnect: true,
		lock: true,
		unlock: true,
		"end recording": true,
		"start recording": true,
	} satisfies Partial<Record<RestrictedDeviceLoggedEvent["name"], boolean>>,
};

const FLAG_LEVEL_MAP = {
	"0": "None",
	"1": "Minor",
	"2": "Major",
	"3": "Severe",
};

export const DEFAULT_FLAG_LEVEL_FILTER: { [key: string]: boolean } = {
	None: false,
	Minor: false,
	Major: false,
	Severe: false,
};

const ForceStopProctoringEventRow = ({ event }: { event: VideoEvent }) => {
	const [modalShowing, setModalShowing] = useState(false);
	return (
		<>
			<span>
				{i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.forceStoppedProctoring")}{" "}
				{event.details?.message && (
					<Link component="button" onClick={() => setModalShowing(true)} sx={{ verticalAlign: "baseline" }}>
						{i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.viewStudentMessage")}
					</Link>
				)}
			</span>
			{event.details?.message && modalShowing && (
				<CoreModal
					open
					onClose={() => setModalShowing(false)}
					title={i18n.t(
						"result.components.studentSelection.videoPlayer.timeStampLinksTSX.viewStudentMessageModalTitle",
					)}
					showBottomCloseButton
				>
					<Typography fontWeight={700} fontSize={18}>
						{i18n.t(
							"result.components.studentSelection.videoPlayer.timeStampLinksTSX.viewStudentMessageModalDescription",
						)}
					</Typography>
					<div
						style={{
							whiteSpace: "pre-line",
							wordBreak: "break-word",
							width: 600,
							maxWidth: "100%",
						}}
					>
						<Typography>{event.details.message}</Typography>
					</div>
				</CoreModal>
			)}
		</>
	);
};

// Decides what is written in the flag snippet
export const processDisplay = (
	event: VideoEvent,
	textOnly = true,
	renderLink = false,
	onOpenExtensions?: () => void,
	onOpenRunningApplications?: () => void,
) => {
	switch (event?.type) {
		case "ephemeral":
			if (event.name === "missing end quiz verification") {
				return i18n.t(
					"result.components.studentSelection.videoPlayer.timeStampLinksTSX.missingEndQuizVerification",
				) as string;
			}
			break;
		case "tab":
			if (event.name === "create") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.openTab");
			} else if (event.name === "disable") {
				if (event.details.success) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.disableTabSuccess");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.disableTabFail");
				}
			} else if (event.name === "close one") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.closeTab");
			} else if (event.name === "focus") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.focusChange");
			} else if (event.name === "whitelist") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.whitelistTab");
			} else if (event.name === "close all") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.closeAllTab");
			}
			break;
		case "user action":
			if (event.name === "side panel detected") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.sidePanelDetected");
			} else if (event.name === "right click blocked") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.rightClickBlocked");
			} else if (event.name === "printing blocked") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.printingBlocked");
			} else if (event.name === "toggled block mobile apps") {
				const enabled = event.details.enabled;
				const osName = event.details.os ? (event.details.os === "android" ? "Android" : "iOS") : "";

				const messageName = `toggledBlockMobileApps${enabled ? "Enabled" : "Disabled"}${osName}`;
				return i18n.t(`result.components.studentSelection.videoPlayer.timeStampLinksTSX.${messageName}`) as string;
			} else if (event.name === "decoy site viewed") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.viewDecoySite");
			} else if (event.name === "typed signature" && event.details.signature) {
				return textOnly ? (
					i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.typedSignature", {
						signature: event.details.signature,
					})
				) : (
					<Trans
						i18nKey="result.components.studentSelection.videoPlayer.timeStampLinksTSX.typedSignature"
						i18nPackage={i18n}
						values={{ signature: event.details.signature }}
						components={{ b: <strong /> }}
					/>
				);
			}
			break;
		case "screenshot":
			if (event.name === "disable") {
				if (event.details.success) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.disableScreenShots");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.disableScreenShotsFail");
				}
			} else if (event.name === "keypress") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.detectKeypress", {
					keys: processKeypressData(event.details.keypress),
				});
			} else if (event.name === "focus change") {
				if (event.details.focus) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.beginScreenshot");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.endScreenshot");
				}
			}
			break;
		case "session":
			if (event.name === "start proctoring") {
				if (event.details.success) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.proctorSessionBegin");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.proctorSessionBeginFail");
				}
			} else if (event.name === "start quiz") {
				if (event.details.success) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.quizStart");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.quizStartFail");
				}
			} else if (event.name === "start recording") {
				if (event.details?.success !== false) {
					if (event.details.media == "webcam") {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.webcamRecordBegin");
					} else if (event.details.media == "display") {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.recordBegin");
					} else {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.micTurnOn");
					}
				} else {
					if (event.details.media == "webcam") {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.startWebcamRecordingFail");
					} else if (event.details.media == "display") {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.startScreenRecordingFail");
					} else {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.startAudioRecordingFail");
					}
				}
			} else if (event.name === "end proctoring") {
				if (event.details.success) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.proctorSessionEnd");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.proctorSessionEndFail");
				}
			} else if (event.name === "end quiz" && event.details.success) {
				if (event.details.submitted === false) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.endQuizWithoutSubmission");
				}
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.endQuiz");
			} else if (event.name === "force stop proctoring") {
				if (textOnly) {
					const baseText = i18n.t(
						"result.components.studentSelection.videoPlayer.timeStampLinksTSX.forceStoppedProctoring",
					);
					if (event.details?.message) return `${baseText} - Student Message: "${event.details.message}"`;
					return baseText;
				}
				return <ForceStopProctoringEventRow event={event} />;
			} else if (event.name === "no submission") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.noSubmission");
			} else if (event.name === "upload incomplete") {
				return i18n.t(
					`result.components.studentSelection.videoPlayer.timeStampLinksTSX.${event.details.media as "webcam" | "display" | "microphone"}UploadIncomplete`,
				);
			} else if (event.name === "end recording" || !event.details.success) {
				if (event.details?.success !== false) {
					if (event.details.media == "webcam") {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.endWebcamRecording");
					} else if (event.details.media == "display") {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.endScreenRecording");
					} else {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.endAudioRecording");
					}
				} else {
					if (event.details.media == "webcam") {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.failEndWebcamRecording");
					} else if (event.details.media == "display") {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.failEndScreenRecording");
					} else {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.failEndAudioRecording");
					}
				}
			}
			break;
		case "monitor":
			if (event.name === "enable detection") {
				if (event.details.success) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.monitorDetectionOn");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.monitorDetectionOnFail");
				}
			} else if (event.name === "detect change") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.changesDetect", {
					number: event.details.number,
				});
			}
			break;
		case "incognito":
			if (event.details.permission) {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.enableIncognito");
			} else {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.enableIncognitoFail");
			}
			break;
		case "clipboard":
			if (event.name === "disable") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.disableClip");
			} else if (event.name === "keypress") {
				const value: string | undefined = event.details.value;
				if (value) {
					if (value === "copy-image") {
						return `${i18n.t(
							"result.components.studentSelection.videoPlayer.timeStampLinksTSX.detectStudentUsingClipboardImage",
							{
								keypress: event.details.keypress,
							},
						)}`;
					} else {
						let displayText;
						if (value.length > 25) {
							displayText = `${value.substring(0, 25)}...`;
						} else {
							displayText = '"' + value + '"';
						}
						return `${i18n.t(
							"result.components.studentSelection.videoPlayer.timeStampLinksTSX.detectStudentUsingClipboardText",
							{
								keypress: event.details.keypress,
								text: displayText,
							},
						)}`;
					}
				}
				return `${i18n.t(
					"result.components.studentSelection.videoPlayer.timeStampLinksTSX.detectStudentUsingClipboard",
					{
						keypress: event.details.keypress,
					},
				)}`;
			} else if (event.name === "clear") {
				if (event.details.success) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.clearClip");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.clearClipFail");
				}
			}
			break;
		case "web navigation":
			const text =
				event.name === "blocked navigation"
					? i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.blockedNavigationAccess")
					: i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.blockedNavigation");
			return renderLink ? (
				<>
					{text}
					<Link className="navigated_link flag_desc_details" href={processLink(event) ?? undefined} target="_blank">
						{processLink(event)}
					</Link>
				</>
			) : (
				text
			);
		case "system":
			if (event.name == "connection check" && event.details.uploadSpeed) {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.checkConnection", {
					speed: event.details.uploadSpeed,
				});
			} else if (event.name === "connection status") {
				if (event.details.connected === true) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.verityConnectionCheck");
				} else if (event.details.connected === false) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.verityConnectionCheckFail");
				}
			} else if (event.name === "block applications" && !event.details.success) {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.blockingApplicationsFailed");
			} else if (event.name === "system resources") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.systemResource", {
					cpu: event.details.cpuUsage,
					memory: event.details.memoryUsage,
				});
			} else if (event.name === "not in geofence") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.notInGeofence");
			} else if (event.name === "location unknown") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.locationUnknown");
			} else if (event.name === "not in ip address range") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.notInIpAddressRange");
			} else if (event.name === "ip address unknown") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.ipAddressUnknown");
			} else if (event.name === "installed extensions snapshot") {
				const extensions = event.details.extensions as { id: string; name: string }[];
				if (textOnly) {
					return i18n.t(
						"result.components.studentSelection.videoPlayer.timeStampLinksTSX.installedExtensionsSnapshot",
						{
							count: extensions.length,
						},
					);
				}

				return (
					<Trans
						i18nKey="result.components.studentSelection.videoPlayer.timeStampLinksTSX.installedExtensionsSnapshot"
						i18nPackage={i18n}
						values={{ count: extensions.length }}
						components={{
							a: <Link component="button" onClick={onOpenExtensions} sx={{ verticalAlign: "baseline" }} />,
						}}
					/>
				);
			} else if (event.name === "running applications snapshot") {
				const applications = event.details.applications as string[];
				if (textOnly) {
					return i18n.t(
						"result.components.studentSelection.videoPlayer.timeStampLinksTSX.runningApplicationsSnapshot",
						{
							count: applications.length,
						},
					) as string;
				}

				return (
					<Trans
						i18nKey="result.components.studentSelection.videoPlayer.timeStampLinksTSX.runningApplicationsSnapshot"
						i18nPackage={i18n}
						values={{ count: applications.length }}
						components={{
							a: <Link component="button" onClick={onOpenRunningApplications} sx={{ verticalAlign: "baseline" }} />,
						}}
					/>
				);
			}
			break;
		case "access code":
			if (event.name == "autofill") {
				if (event.details.success === true) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.inputAccessCode");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.inputAccessCodeFail");
				}
			} else if (event.name == "save password disabled") {
				if (event.details.success === true) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.disableSavePassword");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.disableSavePasswordFail");
				}
			} else if (event.name == "save password reset") {
				if (event.details.success === true) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.resetSavePassword");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.resetSavePasswordFail");
				}
			}
			break;
		case "video_error":
			if (event.name === "video_error") {
				if (event.details.start === true) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.videoDetect");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.videoDetectFail");
				}
			}
			break;
		case "head_movements":
			if (event.name === "head_movements") {
				if (event.details.start === true) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.headMove");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.headMoveEnd");
				}
			}
			break;
		case "interrupt":
			if (event.name === "resume") {
				if (event.details.type === "webcam") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.webcamReconnect");
				} else if (event.details.type === "display") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.screenShareResume");
				} else if (event.details.type === "screenShareInactive") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.screenShareResumeFrozen");
				} else if (event.details.type === "monitor count") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.secondMonitorDisconnect");
				}
			} else if (event.name === "trigger") {
				if (event.details.type === "webcam") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.webcamDisconnect");
				} else if (event.details.type === "display") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.screenShareStop");
				} else if (event.details.type === "screenShareInactive") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.screenShareFrozen");
				} else if (event.details.type === "monitor count") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.secondMonitorDetect");
				} else if (event.details.type === "speaking") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.audioDisturb");
				} else if (event.details.type === "opens_blocked_app") {
					const appNames = event.details.appNames as string[];
					if (!appNames || appNames.length === 0) {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.oldOpensBlockedApp");
					} else if (appNames.length === 1) {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.opensBlockedApp", {
							appName: appNames[0],
						});
					}
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.opensBlockedApps", {
						count: appNames.length,
						appNames: appNames.join(", "),
					});
				} else if (event.details.type === "enables_blocked_extension") {
					const extNames = event.details.extNames as string[];
					if (!extNames || extNames.length === 0) {
						return i18n.t(
							"result.components.studentSelection.videoPlayer.timeStampLinksTSX.safeEnablesBlockedExtension",
						);
					} else if (extNames.length === 1) {
						return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.enablesBlockedExtension", {
							extName: extNames[0],
						});
					}
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.enablesBlockedExtensions", {
						count: extNames.length,
						extName: extNames.join(", "),
					});
				} else if (event.details.type === "handoff") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.enablesHandoff");
				}
			}
			break;
		case "live_session_note":
			if (event.name === "create") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.addNote", {
					name: event.details?.editorName ?? "Proctor",
				});
			}
			break;
		case "keyboard":
			if (event.name === "keypress") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.keystrokeDetected", {
					keystrokes: event.details.value,
				});
			}
			break;
		case "live":
			if (event.name === "audio bridge") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.audioBridgeStart");
			} else if (event.name === "proctor joined") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.proctorJoined");
			} else if (event.name === "proctor left") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.proctorLeft");
			} else if (event.name === "join session") {
				if (event.details.proctorIds?.length === 0) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.joinWithoutProctor");
				}
			} else if (event.name === "no proctors") {
				if (event.details.isOnJoin) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.joinWithoutProctor");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.noProctors");
				}
			}
			break;
		case "face detection":
			if (event.name === "other") {
				if (event.details.count === 0) {
					// return "Detected no other people in the camera view";
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.multiplePeopleDetected");
				}
			} else if (event.name === "target") {
				if (event.details.count === 0) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.faceInvisible");
				} else {
					// return `The test-taker's face has re-entered the camera view`;
				}
			}
			break;
		case "object detection":
			return `Detected a ${event.name} in the camera view`;
		case "worker":
			if (event.name === "face") {
				const numberOfFaces = event.details.detections.length;
				if (numberOfFaces === 0) {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.faceInvisible");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.multiplePeopleDetected");
				}
			} else if (event.name === "object") {
				if (event.details.detections[0].name === "cell phone") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.cellPhoneDetected");
				} else if (event.details.detections[0].name === "laptop") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.laptopDetected");
				} else if (event.details.detections[0].name === "book") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.bookDetected");
				} else {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.objectDetected", {
						object: event.details.detections[0].name,
					});
				}
			}
			break;
		case "machine-learning": {
			if (event.name === "face-detection") {
				const { details } = event as unknown as ZodToReadable<typeof FaceDetectionEventSchema>;

				if (details.currentState === "detected-no-face") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.faceInvisible");
				} else if (details.currentState === "detected-many-faces") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.multiplePeopleDetected");
				} else if (details.currentState === "detected-unknown-face") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.multiplePeopleDetected");
				}
			} else if (event.name === "object-detection") {
				const { details } = event as unknown as ZodToReadable<typeof ObjectDetectionEventSchema>;
				if (details.currentState === "detected-cell-phone") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.cellPhoneDetected");
				} else if (details.currentState === "detected-laptop") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.laptopDetected");
				} else if (details.currentState === "detected-book") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.bookDetected");
				} else if (details.currentState === "detected-many-objects") {
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.objectDetectedMany");
				}
			} else if (event.name === "flagged-keyword") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.flaggedKeyword", {
					keyword: event.details.keyword,
				});
			}
			break;
		}
		case "id": {
			if (event.name === "face id mismatch") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.faceIdMismatch");
			} else if (event.name === "bypassed ai detection") {
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.bypassedAiDetection");
			}
			break;
		}
		case "assessment": {
			if (event.name === "collaboration detected" || event.name === "essay collaboration detected") {
				const details = event.details as
					{ partners: CollaborationPartnerSummary[] } | { partner: CollaborationParticipant };
				if ("partners" in details) {
					// Question-grouped, for the seekbar: one marker per question, naming every partner on it.
					const names = details.partners
						.map((partner) => partner.studentName || partner.studentId)
						.filter(Boolean)
						.join(", ");
					return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.collaborationDetected", {
						name: names,
					}) as string;
				}
				return i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.collaborationDetected", {
					name: details.partner.studentName || details.partner.studentId || "",
				}) as string;
			}
			break;
		}
		case "custom":
			return event.details.message;
		case "restricted-device": {
			return Match.value(event).pipe(
				Match.when({ details: { peer: { role: "source" } } }, () => {
					return Match.value(event).pipe(
						Match.when({ name: "state" }, () => {
							return Match.value(event.details).pipe(
								Match.when({ state: { connected: true, data: { isLocked: false } } }, () =>
									i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.restrictedDeviceUnlocked"),
								),
								Match.when({ state: { connected: true } }, () =>
									i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.restrictedDeviceConnected"),
								),
								Match.when({ state: { connected: false } }, () =>
									i18n.t(
										"result.components.studentSelection.videoPlayer.timeStampLinksTSX.restrictedDeviceDisconnected",
									),
								),
								Match.orElse(() => undefined),
							);
						}),
						Match.when({ name: "connect" }, () =>
							i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.restrictedDeviceConnected"),
						),
						Match.when({ name: "disconnect" }, () =>
							i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.restrictedDeviceDisconnected"),
						),
						Match.when({ name: "unlock" }, () =>
							i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.restrictedDeviceUnlocked"),
						),
						Match.when({ name: "end recording" }, () =>
							i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.webcamDisconnect"),
						),
						Match.orElse(() => undefined),
					);
				}),
				Match.orElse(() => undefined),
			);
		}
	}
};

// Determines the type of flag and decides the picture to put beside the flag snippet
export const processPicture = (event: VideoEvent) => {
	switch (event?.type) {
		case "tab":
			return NewTabSymbol;
		case "screenshot":
			return ScreenshotSymbol;
		case "session":
			if (event.name === "start proctoring") {
				return PlayButton;
			} else if (event.name === "start quiz") {
				return StartQuizSymbol;
			} else if (event.name === "start recording") {
				if (event.details?.success !== false) {
					if (event.details.media == "webcam") {
						return WebcamSymbol;
					} else if (event.details.media == "microphone") {
						return MicrophoneSymbol;
					} else {
						return PlayButton;
					}
				} else {
					if (event.details.media == "webcam") {
						return WebcamEndSymbol;
					} else if (event.details.media == "display") {
						return EndRecordingSymbol;
					} else {
						return MicrophoneSymbol;
					}
				}
			} else if (event.name === "end proctoring") {
				return EndRecordingSymbol;
			} else if (event.name === "end quiz" && event.details.success) {
				return EndQuizSymbol;
			} else if (
				event.name === "force stop proctoring" ||
				event.name === "no submission" ||
				event.name === "upload incomplete"
			) {
				return NoSubmissionSymbol;
			} else {
				return EndRecordingSymbol;
			}
		case "monitor":
			return MonitorSymbol;
		case "incognito":
			return IncognitoSymbol;
		case "clipboard":
			return CopySymbol;
		case "web navigation":
			return WebNavigationSymbol;
		case "system":
			if (event.name == "connection check") {
				return ConnectionCheckSymbol;
			} else if (event.name === "connection status") {
				if (event.details.connected === true) {
					return ConnectionStatusConnectedSymbol;
				} else if (event.details.connected === false) {
					return ConnectionStatusDisconnectedSymbol;
				}
			} else if (event.name === "system resources") {
				return SystemResourcesSymbol;
			} else if (
				event.name === "not in geofence" ||
				event.name === "location unknown" ||
				event.name === "not in ip address range" ||
				event.name === "ip address unknown"
			) {
				return EnableGeofenceSymbol;
			}
			break;
		case "access code":
			return AccessCodeSymbol;
		case "video_error":
			if (event.details.start) {
				return EndRecordingSymbol;
			} else {
				return PlayButton;
			}
		case "interrupt":
			if (event.name === "resume") {
				return InterruptResumeSymbol;
			} else if (event.name === "trigger") {
				return InterruptTriggerSymbol;
			}
			break;
		case "live_session_note":
			return CreateNoteSymbol;
			break;
		case "keyboard":
			if (event.name === "keypress") {
				return KeystrokeSymbol;
			}
			break;
		case "live":
			if (event.name === "audio bridge") {
				return MicrophoneSymbol;
			} else if (event.name === "proctor joined") {
				return EnterSymbol;
			} else if (event.name === "proctor left") {
				return LeaveSymbol;
			} else if (event.name === "join session") {
				if (event.details.proctorIds?.length === 0) {
					return JoinedWithoutProctorSymbol;
				}
			} else if (event.name === "no proctors") {
				if (event.details.isOnJoin) {
					return JoinedWithoutProctorSymbol;
				} else {
					return LeaveSymbol;
				}
			}
			break;
		case "face detection":
			return FaceDetectionSymbol;
		case "object detection":
			return ObjectDetectionSymbol;
		case "worker":
			if (event.name === "face") {
				return FaceDetectionSymbol;
			} else if (event.name === "object") {
				return ObjectDetectionSymbol;
			}
			break;
		case "machine-learning":
			if (event.name === "face-detection") {
				return FaceDetectionSymbol;
			} else if (event.name === "object-detection") {
				return ObjectDetectionSymbol;
			} else if (event.name === "flagged-keyword") {
				return ObjectDetectionSymbol;
			}
			break;
		case "assessment":
			if (event.name === "collaboration detected" || event.name === "essay collaboration detected") {
				return HumanFlagSymbol;
			}
			break;
		case "custom":
			return HumanFlagSymbol;
		case "restricted-device":
			return RestrictDevicesSymbol;
		case "user action":
			if (event.name === "typed signature") {
				return SignatureSymbol;
			} else if (event.name === "toggled block mobile apps") {
				return RestrictDevicesSymbol;
			}
			break;
		default:
			return "";
	}
};

export function isValidEvent(event: VideoEvent) {
	function isKeyOfObject<T extends object>(key: string | number | symbol, obj: T): key is keyof T {
		return key in obj;
	}

	if (event?.type && event?.name && isKeyOfObject(event.type, validEvents) && validEvents[event.type]) {
		if (isKeyOfObject(event.name, validEvents[event.type])) {
			if (event.name === "start quiz" && event.details.fromSetup) return false; // do not show start quiz when it sends from our setup page
			// Should probably remove validEvents in favour of the processData, or refactor with the other process`string` for reusability...
			if (event.type === "tab" && event.name === "focus" && event.details?.focus) return false; // we do not show student focus back on assessment page
			return validEvents[event.type][event.name] && processDisplay(event) !== undefined;
		}
	}
	return false;
}

// Determines whether a flag shows and if the flag level is minor/major/severe
/** One placeholder per severity in {@link FlagFilterLayout}'s own LEVELS, while its counts load. */
const FLAG_FILTER_SKELETON_KEYS: FlagFilterLevel[] = ["Severe", "Major", "Minor"];

export const processSeverity = (event: VideoEvent) => {
	switch (event?.flag_level) {
		case 1:
			return (
				<MinorFlagLevel>{`${i18n.t("result.components.studentSelection.selectionMenuTSX.minor")}`}</MinorFlagLevel>
			);
		case 2:
			return (
				<MajorFlagLevel>{`${i18n.t("result.components.studentSelection.selectionMenuTSX.major")}`}</MajorFlagLevel>
			);
		case 3:
			return (
				<SevereFlagLevel>{`${i18n.t("result.components.studentSelection.selectionMenuTSX.severe")}`}</SevereFlagLevel>
			);
		default:
			return <NoneFlagLevel>{`${i18n.t("result.components.studentSelection.selectionMenuTSX.none")}`}</NoneFlagLevel>;
	}
};

export const getSeverityBackgroundColorFromEvent = (event: VideoEvent): string => {
	switch (event?.flag_level) {
		case 1:
			return "#E7F2E0";
		case 2:
			return "#FFF2CF";
		case 3:
			return "#F4DDDB";
		default:
			return "#E5E5E5";
	}
};

export const getSeverityTextColorFromEvent = (event: VideoEvent): string => {
	switch (event?.flag_level) {
		case 1:
			return "#344054";
		case 2:
			return "#B35300";
		case 3:
			return "#B72C2C";
		default:
			return "#4F5F2E";
	}
};

export const processLink = (event: VideoEvent) => {
	if (
		event?.details &&
		event.details?.url &&
		typeof event.details.url === "string" &&
		event.details.url !== "<no_url>"
	) {
		return event.details.url;
	}
	return null;
};

const formatSecsForTimestamp = (secs: number): string => {
	const hours = Math.floor(secs / 3600);
	const minutes = Math.floor((secs % 3600) / 60);
	const seconds = Math.floor(secs % 60);

	const paddedSeconds = seconds.toString().padStart(2, "0");

	if (hours > 0) {
		const paddedMinutes = minutes.toString().padStart(2, "0");
		return `${hours}:${paddedMinutes}:${paddedSeconds}`;
	}

	return `${minutes}:${paddedSeconds}`;
};

const formatMsForTimestamp = (ms: number): string => {
	const seconds = Math.floor(ms / 1000);
	return formatSecsForTimestamp(seconds);
};

const formatMsForDuration = (ms: number): string => {
	const seconds = Math.floor(ms / 1000);
	return `${seconds.toString()}`;
};

type TimestampLinksProps = {
	sessionId: string;
	events: VideoEvent[];
	dispatch: React.Dispatch<VideoPlaybackAction>;
	arrStamps: TimestampsArr;
	sources: { [key: string]: string };
	offsetGetter: number;
	isLoading: boolean;
	/**
	 * Height of the whole panel (toolbar + table). Takes a CSS length as well as a pixel number so a
	 * caller can pin the panel to a viewport-relative height instead of matching a measured element —
	 * the LMS layout does, so the panel (and the column border beside it) doesn't resize every time the
	 * video area next to it does. The table fills whatever is left below the toolbar either way.
	 */
	panelHeight: number | string;
	duration: number;
	updateEvents: () => Promise<void>;
	summaryButton?: ReactNode;
	downloadMenu: ReactNode;
	/** Counts shown on the {@link FlagFilterLayout} severity quick-filter buttons, next to the filter button. */
	severeCount?: number;
	majorCount?: number;
	minorCount?: number;
	highlightedEventKey: string | undefined;
	setHighlightedEventKey: Dispatch<SetStateAction<string | undefined>>;
	/** Display only: no resolve overlay, no custom-event edit, no collaboration-detected resolve. */
	readOnly?: boolean;
};

/**
 * The row's Resolve/Review action, as a slide-in overlay copied from RowSelectOverlay in
 * reports/shared/report-components.tsx. Rendered inside the row's last cell (the flag badge one) so
 * it enters from the row's right edge and is free to cover the description column on the way in;
 * table cells here already opt out of clipping. The whole overlay is the hit target, not just the
 * button, so the click doesn't have to land precisely. Generic over what it opens — the inline
 * ResolveModal for a flagged event (see FlagResolveOverlay below), or CollaborationDetectedEventRow's
 * own drawer for a collaboration match — the caller supplies the label and click handler, and keeps
 * `active` true for as long as whatever it opened is still showing.
 */
function ResolveRowOverlay({
	label,
	active,
	onActivate,
}: {
	label: string;
	active: boolean;
	onActivate: (e: React.MouseEvent) => void;
}) {
	return (
		<Box
			className={RESOLVE_REVEAL_CLASS}
			onClick={onActivate}
			sx={{
				...resolveRevealBaseSx,
				position: "absolute",
				top: 0,
				bottom: 0,
				right: "8px",
				display: "flex",
				alignItems: "center",
				gap: "8px",
				cursor: "pointer",
				// Held open while whatever this opened is still up: the pointer has to leave the row to
				// reach the dialog/drawer, and focus lands back here when it closes.
				...(active && { opacity: 1, transform: "translateX(0)", pointerEvents: "auto" }),
			}}
		>
			{/* Bare text, like RowSelectOverlay in reports/shared/report-components.tsx — but a real
			    <button> rather than that overlay's tabIndex={-1} Box, because there the row itself
			    activates the same action and here the row's own click seeks the video instead. So this
			    has to stay a tab stop (reached by tabbing once past the row, which reveals it via the
			    row's :focus-within rule) and has to answer to Enter/Space. */}
			<Box
				component="button"
				type="button"
				onClick={onActivate}
				aria-haspopup="dialog"
				sx={{
					background: "none",
					border: "none",
					padding: 0,
					font: "inherit",
					color: "inherit",
					cursor: "pointer",
					"&:focus-visible": {
						outline: "2px solid #0b6f85",
						outlineOffset: "2px",
						borderRadius: "2px",
					},
				}}
			>
				{label}
			</Box>
			<Box sx={{ display: "flex", alignItems: "center" }} aria-hidden="true">
				<CaretRight style={{ width: "14px", height: "14px" }} />
			</Box>
		</Box>
	);
}

/** The standard flagged-event Resolve overlay: opens the inline comment-collecting ResolveModal. */
function FlagResolveOverlay({
	sessionId,
	event,
	updateEvents,
	modalShowing,
	setModalShowing,
}: {
	sessionId: string;
	event: VideoEvent;
	updateEvents: () => Promise<void>;
	/** Owned by the row so the flag badge this slides over can stay hidden for as long as the modal is up. */
	modalShowing: boolean;
	setModalShowing: (value: boolean) => void;
}) {
	return (
		<>
			<ResolveRowOverlay
				label={i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.resolve")}
				active={modalShowing}
				// Stops the row's own onClick, which would otherwise seek the video out from under the modal.
				onActivate={(e) => {
					e.stopPropagation();
					setModalShowing(true);
				}}
			/>
			{modalShowing && (
				<ResolveModal
					onClose={() => setModalShowing(false)}
					comment={event.comment}
					onConfirm={async (comment) => {
						if (event.id) {
							await apiClient.proctoringSession.updateEventFlag({
								proctoringSessionId: sessionId,
								eventId: event.id,
								resolved: true,
								flagLevel: 0,
								comment: comment || undefined,
							});
						}
						await updateEvents();
						setModalShowing(false);
					}}
				/>
			)}
		</>
	);
}

function CommentIcon({ onClick }: { onClick?: () => void }) {
	return (
		<CoreIconButton
			variant="plain"
			onClick={onClick}
			tooltip={i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.hasComment")}
			sx={{ width: 22, height: 22, mt: "2px" }}
		>
			<SvgIcon
				sx={{
					color: "#42296E",
				}}
			>
				<Comment aria-hidden="true" />
			</SvgIcon>
		</CoreIconButton>
	);
}

function getEventDurationMs(event: VideoEvent): number | undefined {
	const duration = event.details?.metadata?.duration ?? event.details?.duration;
	return typeof duration === "number" && duration > 0 ? duration : undefined;
}

function EventTime({ event, timeOffset }: { event: VideoEvent; timeOffset: number }) {
	const displayedStartTimestamp = Math.max(event.time * 1000 - timeOffset, 0);
	return (
		<span
			className="timestamp-button"
			aria-label={`, ${getDurationAriaLabelFromMs(displayedStartTimestamp)}`} // Added comma because screen reader doesn't pause between row # and aria label
		>
			{formatMsForTimestamp(displayedStartTimestamp)}
		</span>
	);
}

export function EventBadge({ chipColor, children }: Pick<CoreChipProps, "chipColor" | "children">) {
	return (
		<CoreChip variant="rectangular" chipColor={chipColor} sx={{ minWidth: "unset" }}>
			{children}
		</CoreChip>
	);
}

export function FlagLevelBadge({ flagLevel }: { flagLevel: number }) {
	switch (flagLevel) {
		case 1:
			return (
				<EventBadge chipColor="#DBE7E1">
					{i18n.t("result.components.studentSelection.selectionMenuTSX.minor")}
				</EventBadge>
			);
		case 2:
			return (
				<EventBadge chipColor="#FAEFDE">
					{i18n.t("result.components.studentSelection.selectionMenuTSX.major")}
				</EventBadge>
			);
		case 3:
			return (
				<EventBadge chipColor="#F8DCDB">
					{i18n.t("result.components.studentSelection.selectionMenuTSX.severe")}
				</EventBadge>
			);
		default:
			return (
				<EventBadge chipColor="#E6E6E6">
					{i18n.t("result.components.studentSelection.selectionMenuTSX.none")}
				</EventBadge>
			);
	}
}

function LabeledEventInfo({ label, children }: { label: ReactNode; children: ReactNode }) {
	return (
		<div>
			<span style={{ fontWeight: 500, color: "black" }}>{label}: </span>
			{children}
		</div>
	);
}

function EventRow({
	sessionId,
	timeOffset,
	duration,
	updateEvents,
	event,
	expanded,
	setExpanded,
	highlighted,
	clearHighlight,
	onClick,
	onOpenExtensions,
	onOpenRunningApplications,
	readOnly,
}: {
	sessionId: string;
	timeOffset: number;
	duration: number;
	updateEvents: () => Promise<void>;
	event: VideoEvent;
	expanded: boolean;
	setExpanded: (value: boolean) => void;
	highlighted: boolean;
	clearHighlight: () => void;
	onClick: () => void;
	onOpenExtensions: () => void;
	onOpenRunningApplications: () => void;
	readOnly?: boolean;
}) {
	const [editModalShowing, setEditModalShowing] = useState(false);
	// Lives here, not in ResolveRowOverlay, so the flag badge below can stay faded while the modal is up.
	const [resolveModalShowing, setResolveModalShowing] = useState(false);

	const rowRef = useRef<HTMLTableRowElement>(null);
	useEffect(() => {
		if (highlighted) {
			rowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
			const timeout = setTimeout(clearHighlight, 4000);
			return () => clearTimeout(timeout);
		}
	}, [highlighted]);

	const [descriptionLines, setDescriptionLines] = useState(0);
	const descriptionContentRef = useRef<HTMLSpanElement>(null);
	const onDescriptionResize = useCallback(() => {
		if (descriptionContentRef.current) {
			setDescriptionLines(descriptionContentRef.current.getClientRects().length);
		}
	}, []);
	const descriptionRef = useResizeListener(onDescriptionResize);

	const detailsId = useId();
	const processedLink = processLink(event);
	const durationMs = getEventDurationMs(event);

	const collaborationRow = useCollaborationRowAction(event, { sessionId, updateEvents }, readOnly);
	// Collaboration-detected events resolve from within their own drawer instead of the generic resolve flow.
	const showResolve =
		!readOnly &&
		!!event.flag_level &&
		!(
			event.type === "assessment" &&
			(event.name === "collaboration detected" || event.name === "essay collaboration detected")
		);
	const showExpand = event.comment || descriptionLines > 2;

	const descriptionContent = (
		<span style={{ whiteSpace: "nowrap" }}>
			{event.resolved && (
				<div
					style={{
						display: "inline-flex",
						alignItems: "center",
						gap: 3,
						paddingRight: 7,
					}}
				>
					<EventBadge chipColor="#DBE7E1">
						{i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.resolved")}
					</EventBadge>
				</div>
			)}
			<span style={{ whiteSpace: "normal" }} ref={descriptionContentRef}>
				{collaborationRow
					? collaborationRow.description
					: processDisplay(event, false, false, onOpenExtensions, onOpenRunningApplications)}
				{durationMs !== undefined &&
					` ${i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.forDurationSeconds", {
						duration: formatMsForDuration(durationMs),
					})}`}
			</span>
			{processedLink && (
				<Link className="navigated_link flag_desc_details" href={processedLink} target="_blank">
					{processedLink}
				</Link>
			)}
		</span>
	);

	return (
		<>
			<CompositeTableBodyRow
				role="group"
				ref={rowRef}
				sx={resolveRevealHoverSx}
				style={{
					backgroundColor: highlighted ? "rgba(0, 0, 0, 0.04)" : undefined,
					cursor: event.details?.hideTimestamp ? "default" : "pointer",
				}}
				aria-label={
					event.details?.hideTimestamp
						? String(processDisplay(event, true) ?? "")
						: i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.tableRowAriaLabel", {
								time: getDurationAriaLabelFromMs(Math.max(event.time * 1000 - timeOffset, 0)),
							})
				}
				onClick={event.details?.hideTimestamp ? undefined : onClick}
			>
				<CompositeTableCell
					style={{
						verticalAlign: "top",
						overflow: "visible",
						paddingLeft: showExpand ? "10px" : "0px",
						paddingRight: 0,
					}}
				>
					<div
						style={{
							height: 24,
							display: "flex",
							alignItems: "flex-end",
							paddingLeft: showExpand ? "0px" : "15px",
						}}
					>
						{showExpand && (
							<CoreIconButton
								variant="plain"
								color="black"
								onClick={(e) => {
									e.stopPropagation();
									setExpanded(!expanded);
								}}
								tooltip={
									expanded
										? i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.collapse")
										: i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.expand")
								}
								aria-expanded={expanded}
								aria-controls={detailsId}
								sx={{ width: 22, height: 22, mt: "2px" }}
							>
								<SvgIcon style={{ fontSize: 10 }}>
									{expanded ? <CaretDown aria-hidden="true" /> : <CaretRight aria-hidden="true" />}
								</SvgIcon>
							</CoreIconButton>
						)}
						<div style={{ height: 24, display: "flex", alignItems: "center" }}>
							{!event.details?.hideTimestamp && (
								<div style={{ marginTop: 2, marginRight: 3 }}>
									<EventTime event={event} timeOffset={timeOffset} />
								</div>
							)}
							{event.comment && <CommentIcon onClick={() => setExpanded(!expanded)} />}
						</div>
					</div>
				</CompositeTableCell>
				<CompositeTableCell style={{ verticalAlign: "top", overflow: "visible", paddingLeft: 0, paddingRight: 10 }}>
					<div
						style={{
							width: "100%",
							display: "flex",
							alignItems: "start",
							gap: 10,
							flexWrap: "wrap",
							whiteSpace: "normal",
						}}
					>
						<div
							id={detailsId}
							style={{
								flex: 1,
								minWidth: "min(250px, 100%)",
								fontSize: 14,
								fontWeight: 400,
								color: "#6B6B6B",
								wordWrap: "break-word",
								...(expanded && event.comment
									? {
											display: "flex",
											flexDirection: "column",
											alignItems: "stretch",
											gap: 5,
										}
									: {}),
								...(!expanded
									? {
											display: "-webkit-box",
											lineClamp: 2,
											WebkitLineClamp: 2,
											WebkitBoxOrient: "vertical",
											overflow: "hidden",
											textOverflow: "ellipsis",
											// Room for a link's focus outline, which `overflow: hidden` would clip. The bottom
											// stays tight when lines are clamped, or the hidden third line would show through.
											padding: descriptionLines > 2 ? "4px 4px 0" : 4,
											margin: descriptionLines > 2 ? "-4px -4px 0" : -4,
										}
									: {}),
							}}
							ref={descriptionRef}
						>
							{expanded && event.comment ? (
								<>
									<div>{descriptionContent}</div>
									<LabeledEventInfo
										label={i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.comment")}
									>
										{event.comment}
									</LabeledEventInfo>
								</>
							) : (
								descriptionContent
							)}
						</div>
						<div
							style={{
								display: "flex",
								alignItems: "start",
								gap: 10,
							}}
						>
							{!readOnly && event.id != null && event.type === "custom" && (
								<CoreIconButton
									onClick={(e) => {
										e.stopPropagation();
										setEditModalShowing(true);
									}}
									tooltip={i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.edit")}
									aria-haspopup="dialog"
								>
									<SvgIcon>
										<Pencil aria-hidden="true" />
									</SvgIcon>
								</CoreIconButton>
							)}
						</div>
					</div>
				</CompositeTableCell>
				<CompositeTableCell style={{ verticalAlign: "top", overflow: "visible", paddingLeft: 0, paddingRight: 10 }}>
					<Box sx={{ position: "relative", width: "100%", height: "100%" }}>
						<Box
							// Only fades on rows that have something to reveal in its place — unlike
							// report-components, where selectability is a property of the whole table.
							className={showResolve || collaborationRow ? RESOLVE_REVEAL_HIDE_CLASS : undefined}
							sx={{
								height: 24,
								display: "flex",
								alignItems: "center",
								transition: `opacity ${RESOLVE_REVEAL_TRANSITION_MS}ms ease-out`,
								// The class above only fades this out while the row is hovered or holds focus, and
								// an open modal/drawer is neither — the pointer has moved to the dialog and focus is
								// trapped inside it. Without this the badge fades back in underneath the overlay,
								// which stays held open, and the two render on top of each other.
								...((resolveModalShowing || collaborationRow?.action.active) && { opacity: 0 }),
							}}
						>
							<FlagLevelBadge flagLevel={event.flag_level} />
						</Box>
						{showResolve && (
							<FlagResolveOverlay
								sessionId={sessionId}
								event={event}
								updateEvents={updateEvents}
								modalShowing={resolveModalShowing}
								setModalShowing={setResolveModalShowing}
							/>
						)}
						{collaborationRow && (
							<ResolveRowOverlay
								label={collaborationRow.action.label}
								active={collaborationRow.action.active}
								onActivate={collaborationRow.action.onActivate}
							/>
						)}
					</Box>
				</CompositeTableCell>
			</CompositeTableBodyRow>
			{editModalShowing && event.id != null && (
				<EditCustomEventModal
					onClose={() => setEditModalShowing(false)}
					event={event}
					onSave={async ({ message, flagLevel }) => {
						if (event.id != null) {
							const updates: Promise<unknown>[] = [];
							if (message !== (event.details?.message ?? "")) {
								updates.push(
									apiClient.proctoringSession.updateCustomEvent({
										proctoringSessionId: sessionId,
										eventId: event.id,
										message,
									}),
								);
							}
							if (flagLevel !== event.flag_level) {
								if (!event.flag_level && flagLevel) {
									updates.push(
										apiClient.proctoringSession.createCustomEventFlag({
											proctoringSessionId: sessionId,
											eventId: event.id,
											flagLevel,
										}),
									);
								} else {
									updates.push(
										apiClient.proctoringSession.updateEventFlag({
											proctoringSessionId: sessionId,
											eventId: event.id,
											flagLevel,
										}),
									);
								}
							}
							await Promise.all(updates);
							await updateEvents();
						}
					}}
					formatTime={(time) => formatEventTimestamp(Math.max(time * 1000 - timeOffset, 0), duration)}
				/>
			)}
		</>
	);
}

export const TimestampLinks = ({
	sessionId,
	events,
	dispatch,
	arrStamps,
	sources,
	offsetGetter,
	isLoading,
	panelHeight,
	duration,
	updateEvents,
	summaryButton,
	downloadMenu,
	severeCount,
	majorCount,
	minorCount,
	highlightedEventKey,
	setHighlightedEventKey,
	readOnly,
}: TimestampLinksProps) => {
	const hiddenVideoRef = useRef<HTMLVideoElement>(null);

	function button_skip(event: VideoEvent) {
		if (event.time * 1000 < offsetGetter) {
			return offsetGetter;
		} else {
			return event.time * 1000;
		}
	}

	function skip_timestamp(event: VideoEvent) {
		dispatch({ type: "skip", time: button_skip(event) });
		const modalBody = document.getElementById("modalBody");
		if (modalBody) {
			modalBody.scrollTop = 0;
		}
	}

	const [thumbnailVideoIndex, setThumbnailVideoIndex] = useState(0);
	// set to -2 to load the 0th time
	const [sessionStartEventIndex, setSessionStartEventIndex] = useState(0);
	const [sessionEndEventIndex, setSessionEndEventIndex] = useState(0);

	const drawingThumbnails = useRef(false);
	type ThumbnailState =
		"black" | "startEmpty" | "endEmpty" | "drawn" | "unavailable" | "processing" | "loading" | "videoAnomaly";
	const [thumbnailStates, setThumbnailStates] = useState<ThumbnailState[]>(events.map(() => "loading"));

	const loadThumbnails = async () => {
		if (hiddenVideoRef.current && events && !drawingThumbnails.current) {
			drawingThumbnails.current = true;
			const thumbnailStatesTemp = thumbnailStates;
			let isThumbnailVideoErrorEvent = false;
			let currentSource = sources?.[0];
			for (const [index, event] of events.entries()) {
				// set initial thumbnail state
				const sessionEndThumbnailThreshold = 3;
				let state: ThumbnailState = "black";
				if (index <= sessionStartEventIndex) {
					state = "startEmpty";
				} else if (index >= sessionEndEventIndex) {
					state = "endEmpty";
				} else if (event.time >= (events?.[sessionEndEventIndex]?.time ?? 0) - sessionEndThumbnailThreshold) {
					state = "endEmpty";
				}

				// set video time
				const currentTime = Math.max(event.time, offsetGetter / 1000);
				const currentEventStamp = arrStamps[binSearch(arrStamps, currentTime)];
				// video source switch, set to next video index
				if (currentEventStamp.vid > thumbnailVideoIndex) {
					if (currentEventStamp.vid) {
						setThumbnailVideoIndex(currentEventStamp.vid as number);
						currentSource = sources?.[arrStamps?.[binSearch(arrStamps, currentTime)]?.vid as string];
						hiddenVideoRef.current.src = currentSource;
					}
				}
				// set video time for next thumbnail and trigger next thumbnail load
				let diff = currentTime - (currentEventStamp.start ?? 0);
				// if timestamp before or after its video's times, use black screen
				if (currentTime < (currentEventStamp.start ?? 0) || currentTime > (currentEventStamp.end ?? 0)) diff = -1;
				// use diff == 0 to signify black screen since currentTime clamps to video's times
				const videoLoaded = await loadVideoAtTime(hiddenVideoRef.current, diff < 0 ? 0 : diff < 0.1 ? 0.1 : diff).catch(
					() => false,
				);

				// replace skeleton
				drawBlankThumbnail(index);
				// set processing or unavailable based on video error
				const error = hiddenVideoRef.current?.error;
				if (error) {
					if (sources && currentSource && error.code === error.MEDIA_ERR_SRC_NOT_SUPPORTED) {
						state = "processing";
					} else {
						state = "unavailable";
					}
					thumbnailStatesTemp[index] = state;
					setThumbnailStates(thumbnailStatesTemp);
					continue;
				}
				// draw thumbnail from video
				if (videoLoaded && hiddenVideoRef.current && hiddenVideoRef.current.currentTime >= 0.1) {
					drawThumbnail(hiddenVideoRef.current, index);
					state = "drawn";
				}
				// set videoAnomaly state based on event
				if (event.type === "interrupt") {
					isThumbnailVideoErrorEvent = event.details.type === "webcam";
				}
				if (isThumbnailVideoErrorEvent) {
					state = "videoAnomaly";
				}
				if (event.type === "interrupt" && event.details.type === "webcam") {
					isThumbnailVideoErrorEvent = event.name === "trigger";
				}
				// save thumbnail states
				thumbnailStatesTemp[index] = state;
				setThumbnailStates(thumbnailStatesTemp);
			}
			setThumbnailStates(thumbnailStatesTemp);
			drawingThumbnails.current = false;
		}
	};

	function loadVideoAtTime(videoEl: HTMLVideoElement, time: number) {
		return new Promise((resolve, reject) => {
			if (videoEl.error) {
				reject();
			}
			videoEl.addEventListener("canplay", function canPlayListener() {
				videoEl.removeEventListener("canplay", canPlayListener);
				resolve(true);
			});
			videoEl.addEventListener("error", function errorListener() {
				videoEl.removeEventListener("error", errorListener);
				reject();
			});
			setTimeout(() => resolve(false), 3000);

			videoEl.currentTime = time;
		});
	}

	function drawThumbnail(video: HTMLVideoElement | null, index: number) {
		if (!video) return;
		if (video?.videoHeight == 0 && video?.videoWidth == 0) return;
		const canvas = document.getElementById(`thumbnail_pic_${index}`);
		const fill = document.getElementById(`thumbnail_fill_${index}`);
		if (canvas instanceof HTMLCanvasElement) {
			if (fill) {
				fill.style.display = "none";
			}
			canvas.width = video?.videoWidth ?? 0;
			canvas.height = video?.videoHeight ?? 0;
			canvas.getContext("2d")?.drawImage(video, 0, 0, canvas.width, canvas.height);
		}
	}

	function drawBlankThumbnail(index: number) {
		const fill = document.getElementById(`thumbnail_fill_${index}`);
		const skeleton = document.getElementById(`thumbnail_skeleton_${index}`);
		if (fill) {
			fill.style.display = "block";
		}
		if (skeleton) {
			skeleton.style.display = "none";
		}
	}

	// get proctoring start and end event indices
	useEffect(() => {
		for (const [index, event] of events.entries()) {
			if (event.name === "start proctoring") {
				setSessionStartEventIndex(index);
			} else if (event.name === "end proctoring") {
				setSessionEndEventIndex(index);
			}
		}
	}, [events]);

	const [eventFilter, setEventFilter] = useState(
		events.reduce(
			(acc, event) => {
				FILTER_EVENTS.forEach((option) => {
					if (option.check(event)) {
						acc[option.name] = false;
					}
				});
				return acc;
			},
			{} as { [key: string]: boolean },
		),
	);
	// Shared by the severity quick-filter buttons and the filter popover's flag-level checkboxes, so
	// either control reflects the other.
	const [flagLevelFilter, setFlagLevelFilter] = useState<{ [key: string]: boolean }>(DEFAULT_FLAG_LEVEL_FILTER);

	const [extensionModalOpen, setExtensionModalOpen] = useState(false);
	const [runningApplicationsModalOpen, setRunningApplicationsModalOpen] = useState(false);
	const [sortBy, setSortBy] = useState<{ key: TableSortBy; direction: "asc" | "desc" }>({
		key: "timestamp",
		direction: "asc",
	});

	const ephemeralEvents = useMemo(() => {
		const ephemeralEvents: VideoEvent[] = [];

		const hasEndQuizEvent = events.some((event) => {
			return event.name === "end quiz";
		});
		if (!hasEndQuizEvent) {
			ephemeralEvents.push({
				key: "missing-end-quiz-verification",
				name: "missing end quiz verification",
				type: "ephemeral",
				time: duration / 1000,
				details: {
					hideTimestamp: true,
				},
				flag_level: 0,
				logged_time: "",
				resolved: false,
			});
		}

		return ephemeralEvents;
	}, [events, duration]);

	const mergedEvents = useMemo(() => {
		return [...events, ...ephemeralEvents];
	}, [events, ephemeralEvents]);

	const filteredEventsData = useMemo(() => {
		const isAnyFlagFilterSelected = Object.values(flagLevelFilter).some((selected) => selected);
		const isAnyEventFilterSelected = Object.values(eventFilter).some((selected) => selected);

		const filteredEvents = mergedEvents
			.filter(isValidEvent)
			.filter((event) => {
				if (!isAnyFlagFilterSelected) return true;
				return flagLevelFilter?.[FLAG_LEVEL_MAP[event.flag_level?.toString() as keyof typeof FLAG_LEVEL_MAP]];
			})
			.filter((event) => {
				if (!isAnyEventFilterSelected) return true;
				return FILTER_EVENTS.some((option) => option.check(event) && eventFilter[option.name]);
			});

		// A "collaboration detected" event is logged once per (question, partner) pair; the table
		// surfaces one row per partner, the seekbar surfaces one marker per question (see SeekbarEvents).
		const tableEvents = groupCollaborationEventsByPartner(filteredEvents);

		const { key, direction } = sortBy;
		switch (key) {
			case "timestamp":
				tableEvents.sort((a, b) => a.time - b.time);
				break;
			case "description":
				tableEvents.sort((a, b) => String(processDisplay(a) ?? "").localeCompare(String(processDisplay(b) ?? "")));
				break;
			case "level":
				tableEvents.sort((a, b) => (a.flag_level ?? 0) - (b.flag_level ?? 0));
				break;
		}
		if (direction === "desc") {
			tableEvents.reverse();
		}
		return tableEvents;
	}, [mergedEvents, flagLevelFilter, eventFilter, sortBy]);
	const [expandedEventKey, setExpandedEventKey] = useState<string | undefined>(undefined);

	useEffect(() => {
		setEventFilter(
			mergedEvents.reduce(
				(acc, event) => {
					FILTER_EVENTS.forEach((option) => {
						if (option.check(event)) {
							acc[option.name] = false;
						}
					});
					return acc;
				},
				{} as { [key: string]: boolean },
			),
		);
		setFlagLevelFilter(DEFAULT_FLAG_LEVEL_FILTER);
	}, [mergedEvents, setFlagLevelFilter]);

	const tableHeaderColumns: CompositeTableHeaderColumn<
		"timestamp" | "description" | "level",
		"timestamp" | "description" | "level"
	>[] = [
		{
			key: "timestamp",
			content: i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.eventTimestamp"),
			sortable: true,
			width: 147,
			style: { paddingLeft: 15, paddingRight: 20 },
		},
		{
			key: "description",
			content: i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.eventDescription"),
			sortable: true,
			style: { paddingLeft: 0 },
		},
		{
			key: "level",
			content: i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.eventLevel"),
			sortable: true,
			width: 110,
			style: { paddingLeft: 0 },
		},
	];

	const installedExtensions = useMemo(() => {
		const extensionEvent = events.find(
			(event) => event.type === "system" && event.name === "installed extensions snapshot",
		);
		const extensions = extensionEvent?.details?.extensions as { id: string; name: string }[] | undefined;

		return Array.isArray(extensions) ? extensions.map((extension) => extension.name) : [];
	}, [events]);

	const runningApplications = useMemo(() => {
		const applicationsEvent = events.find(
			(event) => event.type === "system" && event.name === "running applications snapshot",
		);
		const applications = applicationsEvent?.details?.applications as string[] | undefined;

		return Array.isArray(applications) ? applications : [];
	}, [events]);

	const EventFlagFilterButton = () => {
		const [localFlagFilter, setLocalFlagFilter] = useState({ ...flagLevelFilter });
		const [localEventFilter, setLocalEventFilter] = useState({ ...eventFilter });
		const count =
			Object.values(flagLevelFilter).filter((v) => v).length + Object.values(eventFilter).filter((v) => v).length;
		const flagLevelNames: Record<string, string> = {
			None: i18n.t("result.components.studentSelection.selectionMenuTSX.none"),
			Minor: i18n.t("result.components.studentSelection.selectionMenuTSX.minor"),
			Major: i18n.t("result.components.studentSelection.selectionMenuTSX.major"),
			Severe: i18n.t("result.components.studentSelection.selectionMenuTSX.severe"),
		};

		const filterSections: CoreFilterSectionOption[] = [
			{
				alignment: "top",
				sectionTitle: i18n.t("result.components.resultsPage.flagLevelComboBoxName"),
				checkboxes: Object.keys(flagLevelNames).map((level) => ({
					checked: !!localFlagFilter[level],
					onChange: (checked) => setLocalFlagFilter((prev) => ({ ...prev, [level]: checked })),
					children: flagLevelNames[level],
				})),
			},
			{
				alignment: "top",
				sectionTitle: i18n.t("result.components.resultsPage.eventComboBoxName"),
				checkboxes: getEventFilterCheckboxes(mergedEvents, localEventFilter, setLocalEventFilter),
			},
		];

		const handleApply = () => {
			setFlagLevelFilter(localFlagFilter);
			setEventFilter(localEventFilter);
		};

		const handleClear = () => {
			const resetFlags = Object.fromEntries(Object.keys(localFlagFilter).map((key) => [key, false]));
			setLocalFlagFilter(resetFlags);

			const resetEvents = Object.fromEntries(Object.keys(localEventFilter).map((key) => [key, false]));
			setLocalEventFilter(resetEvents);
		};

		const handleOpen = () => {
			setLocalFlagFilter({ ...flagLevelFilter });
			setLocalEventFilter({ ...eventFilter });
		};

		return (
			<CoreFilterButton
				variant="button"
				alternateIcon
				filterSections={filterSections}
				filterCount={count}
				onApply={handleApply}
				onClear={handleClear}
				onOpen={handleOpen}
			/>
		);
	};

	return (
		<>
			<video
				width="120"
				height="76.5"
				controls
				hidden
				id="hiddenVideo"
				className="hiddenVideo"
				src={`${sources?.[0]}`}
				onLoadStart={() => {
					setThumbnailStates(thumbnailStates.map((x) => (["drawn", "blank"].includes(x) ? x : "loading")));
					void loadThumbnails();
				}}
				onError={(e) => {
					const error = e.currentTarget.error;
					if (
						sources &&
						Object.keys(sources).length !== 0 &&
						sources?.[0] &&
						error &&
						error.code === error.MEDIA_ERR_SRC_NOT_SUPPORTED
					) {
						setThumbnailStates(thumbnailStates.map((x) => (x !== "loading" ? x : "processing")));
					} else {
						setThumbnailStates(thumbnailStates.map((x) => (x !== "loading" ? x : "unavailable")));
					}
				}}
				ref={hiddenVideoRef}
			/>
			<div
				tabIndex={-1}
				style={{
					display: "flex",
					flexDirection: "column",
					width: "100%",
					height: panelHeight,
					minHeight: "65vh",
				}}
				className="video-tabpanel"
			>
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						gap: 20,
						rowGap: 10,
						flexWrap: "wrap",
						backgroundColor: "#FFFFFF",
						borderTop: "1px solid #E0E0E0",
						padding: "15px 10px",
						// The table below it is what absorbs the panel's height; this keeps its own (which grows as
						// its controls wrap) rather than being squeezed by that.
						flexShrink: 0,
					}}
				>
					{/* Every control in this bar skeletons off the same flag as the table below and as the page
					    around it, so the whole view resolves in one step. Sizes mirror each control's own: the
					    filter and severity buttons are 35px tall (CoreFilterButton, FlagFilterLayout), the
					    severity ones are three separate bars so the placeholder reads as three buttons. */}
					<div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
						<CoreSkeletonWrapper loading={isLoading} width={42} height={35}>
							<EventFlagFilterButton />
						</CoreSkeletonWrapper>
						{isLoading ? (
							<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
								{FLAG_FILTER_SKELETON_KEYS.map((key) => (
									<CoreSkeletonWrapper key={key} loading width={96} height={35}>
										<div />
									</CoreSkeletonWrapper>
								))}
							</div>
						) : (
							<FlagFilterLayout
								severeCount={severeCount}
								majorCount={majorCount}
								minorCount={minorCount}
								flagLevelFilter={flagLevelFilter}
								onToggle={(level) => setFlagLevelFilter((prev) => ({ ...prev, [level]: !prev[level] }))}
							/>
						)}
					</div>

					<div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
						{summaryButton && (
							<CoreSkeletonWrapper loading={isLoading} width={135} height={35}>
								{summaryButton}
							</CoreSkeletonWrapper>
						)}

						{downloadMenu && (
							<CoreSkeletonWrapper loading={isLoading} width={44} height={38}>
								{downloadMenu}
							</CoreSkeletonWrapper>
						)}
					</div>

					{/* Independent of the kebab menu above — also opened from a "view extensions" link inside an
					    event's own description (see EventRow's onOpenExtensions). */}
					<InstalledExtensionsModal
						open={extensionModalOpen}
						onClose={() => setExtensionModalOpen(false)}
						extensions={installedExtensions}
					/>
					<RunningApplicationsModal
						open={runningApplicationsModalOpen}
						onClose={() => setRunningApplicationsModalOpen(false)}
						applications={runningApplications}
					/>
				</div>
				<CompositeTableContainer className="event-timestamps-table">
					{isLoading ? (
						<CompositeTableSkeleton
							aria-label={
								i18n.t(
									"result.components.studentSelection.videoPlayer.timeStampLinksTSX.eventsTableAriaLabel",
								) as string
							}
							columns={[
								{ key: "expand", content: null, width: 22, style: { padding: 0, paddingLeft: 10 } },
								...tableHeaderColumns.map((column, index) => ({
									...column,
									style: {
										paddingLeft: index === 0 ? 22 : 10,
										paddingRight: index === tableHeaderColumns.length - 1 ? 20 : 10,
									},
								})),
							]}
							rows={7}
						/>
					) : (
						<>
							<CompositeTable
								aria-label={
									i18n.t(
										"result.components.studentSelection.videoPlayer.timeStampLinksTSX.eventsTableAriaLabel",
									) as string
								}
								stickyHeader
								head={
									filteredEventsData.length > 0 ? (
										<CompositeTableHeaderRow
											columns={tableHeaderColumns}
											sortRequired
											sortBy={sortBy}
											onSortByChange={setSortBy}
										/>
									) : (
										<CompositeTableHeaderRow columns={tableHeaderColumns} />
									)
								}
								style={{ tableLayout: "fixed" }}
							>
								{filteredEventsData.map((event) => (
									<EventRow
										key={event.key}
										readOnly={readOnly}
										sessionId={sessionId}
										timeOffset={offsetGetter}
										duration={duration}
										updateEvents={updateEvents}
										event={event}
										expanded={expandedEventKey === event.key}
										setExpanded={(expanded) => setExpandedEventKey(expanded ? event.key : undefined)}
										highlighted={highlightedEventKey === event.key}
										clearHighlight={() =>
											setHighlightedEventKey((currHighlightedEventKey) =>
												currHighlightedEventKey === event.key ? undefined : currHighlightedEventKey,
											)
										}
										onClick={() => skip_timestamp(event)}
										onOpenExtensions={() => setExtensionModalOpen(true)}
										onOpenRunningApplications={() => setRunningApplicationsModalOpen(true)}
									/>
								))}
							</CompositeTable>
							{filteredEventsData.length === 0 && (
								<CompositeTableNoResults>
									{i18n.t("result.components.studentSelection.videoPlayer.timeStampLinksTSX.noEvents")}
								</CompositeTableNoResults>
							)}
						</>
					)}
				</CompositeTableContainer>
			</div>
		</>
	);
};

export default TimestampLinks;
